/**
 * What the two workspace screens - the mail desk and the call desk - have to
 * know about a phone.
 *
 * Both are the same three-pane shape (rail, list, reading pane), and neither
 * shape fits a 390px screen: three columns that need 212 + 300 + 380px cannot
 * be squeezed, and stacking them puts two panes of chrome above the one thing
 * the operator opened. So below `WORK_ONE_PANE_MQ` the screens show ONE pane at
 * a time and the rail becomes a sheet slid in over them.
 *
 * Which pane is showing is not state - it is the route. Both sections already
 * carry the open item in the path (`/email/:id`, `/calls/:id`), so a phone
 * needs no new switch: no id means the list, an id means the item, and the
 * browser's back button walks between them for free. `workView()` is that rule
 * written once, and the value lands on `.crm-work` as `data-view` for the CSS
 * to read.
 *
 * The breakpoint is deliberately the same number as the app sidebar's
 * (`shell/navDrawer.ts`, `NAV_DRAWER_MQ`): one width at which this app stops
 * being a set of columns, not two that nearly agree. It is mirrored by the
 * `max-width: 760px` block in index.css - change one, change the other.
 */
import { useIsNarrow, useNavDrawer } from "../../../shell/navDrawer";

export type WorkView = "list" | "detail";

/** Which pane a phone should be showing. `null` means nothing is open. */
export function workView(openId: string | null | undefined): WorkView {
  return openId ? "detail" : "list";
}

/**
 * The section rail's open state while it is a sheet.
 *
 * `useNavDrawer` already has the three ways out a panel over the page needs - a
 * route change, Escape, and the viewport growing back - and they are the same
 * three here, so this is that hook with the workspace's own name on it rather
 * than a second copy of the same effects. Picking a folder is NOT a route
 * change, so the rail closes that case itself (see `onPicked`).
 */
export function useRailSheet(pathname: string): [boolean, (open: boolean) => void] {
  return useNavDrawer(useIsNarrow(), pathname);
}
